import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  ApiGetBriefTitleList,
  ApiGetBriefIconList,
  ApiAddBriefTitle,
  ApiUpdateBriefTitle
} from '../../../apiService/Core/Apicall.tsx';
import { setLoadingFalse, setLoadingTrue } from '../../../global/config/Action/loadingActions.ts';
import store from '../../../global/config/store.ts';
import PatchedReactQuill from '../ManageVenue/PatchedReactQuill.tsx';

interface BriefIcon {
  briefIconId: number;
  iconName: string;
  iconImage: string;
}

interface BriefTitle {
  briefTitleId: number;
  title: string;
  kicker?: string;
  defaultDescription: string;
  isActive: boolean;
  briefIconId: BriefIcon | number;
  toneColor?: string;
}

const tones = [
  { label: 'Default', value: '#7C9FB2' },
  { label: 'Warning', value: '#FFB74D' },   
  { label: 'Critical', value: '#BF613B' },  
  { label: 'Success', value: '#A5D6A7' }
];

const BriefTitleList: React.FC = () => {
  const [titles, setTitles] = useState<BriefTitle[]>([]);
  const [icons, setIcons] = useState<BriefIcon[]>([]);
  const [search, setSearch] = useState<string>('');
  const [filteredTitles, setFilteredTitles] = useState<BriefTitle[]>([]);
  
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedTitle, setSelectedTitle] = useState<BriefTitle | null>(null);
  
  const [editorVal, setEditorVal] = useState<string>('');

  const fetchIcons = async () => {
    try {
      const response = await ApiGetBriefIconList('?isActive=true');
      const data = response?.data || response || [];
      setIcons(data);
    } catch (error: any) {
      console.error('Error fetching brief icons:', error);
    }
  };

  const fetchTitles = async () => {
    try {
      const response = await ApiGetBriefTitleList('?isActive=true');
      const data = response?.data || response || [];
      setTitles(data);
    } catch (error: any) {
      console.error('Error fetching brief titles:', error);
      toast.error(error || 'Failed to fetch brief titles');
    }
  };

  useEffect(() => {
    fetchIcons();
    fetchTitles();
  }, []);

  useEffect(() => {
    if (titles) {
      const filtered = titles.filter((item) =>
        item.title?.toLowerCase().includes(search.toLowerCase()) ||
        item.kicker?.toLowerCase().includes(search.toLowerCase()) ||
        stripHtml(item.defaultDescription).toLowerCase().includes(search.toLowerCase())
      );
      setFilteredTitles(filtered);
    }
  }, [search, titles]);

  const handleEditClick = async (id: number) => {
    try {
      store.dispatch(setLoadingTrue());
      const response = await ApiGetBriefTitleList(`?briefTitleId=${id}&isActive=true`);
      const details = response?.data?.[0] || response?.data || response;
      
      if (details) {
        setSelectedTitle(details);
        setEditorVal(details.defaultDescription || '');
        setIsEditMode(true);
        setOpenDialog(true);
      } else {
        toast.error('Could not fetch Brief Title details');
      }
    } catch (error: any) {
      toast.error(error || 'Failed to fetch Brief Title details');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const handleDeleteClick = async (id: number) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this Brief Title?');
    if (isConfirmed) {
      try {
        const payload = {
          briefTitleId: id,
          isActive: false
        };
        const response = await ApiUpdateBriefTitle(payload);
        toast.success(response.message || 'Brief Title deleted successfully');
        fetchTitles();
      } catch (error: any) {
        toast.error(error || 'Failed to delete Brief Title');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setSelectedTitle(null);
    setEditorVal('');
    setIsEditMode(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    formik.resetForm();
    setEditorVal('');
  };

  // Formik & Yup validation
  const validationSchema = Yup.object({
    title: Yup.string().trim().required('Title is required'),
    briefIconId: Yup.number().required('Brief Icon is required'),
    defaultDescription: Yup.string().required('Default Description is required'),
    toneColor: Yup.string().required()
  });

  const onSubmit = async (values: { title: string; kicker: string; briefIconId: string | number; defaultDescription: string; toneColor: string }) => {
    try {
      if (isEditMode && selectedTitle) {
        const payload = {
          briefTitleId: selectedTitle.briefTitleId,
          title: values.title.trim(),
          kicker: values.kicker.trim(),
          briefIconId: Number(values.briefIconId),
          defaultDescription: values.defaultDescription,
          toneColor: values.toneColor,
          isActive: true
        };
        const response = await ApiUpdateBriefTitle(payload);
        toast.success(response.message || 'Brief Title updated successfully');
      } else {
        const payload = {
          title: values.title.trim(),
          kicker: values.kicker.trim(),
          briefIconId: Number(values.briefIconId),
          defaultDescription: values.defaultDescription,
          toneColor: values.toneColor
        };
        const response = await ApiAddBriefTitle(payload);
        toast.success(response.message || 'Brief Title added successfully');
      }
      handleCloseDialog();
      fetchTitles();
    } catch (error: any) {
      toast.error(error || 'Failed to save Brief Title');
    }
  };

  const formik = useFormik({
    initialValues: {
      title: selectedTitle?.title || '',
      kicker: selectedTitle?.kicker || '',
      briefIconId: (selectedTitle?.briefIconId as BriefIcon)?.briefIconId || selectedTitle?.briefIconId || '',
      defaultDescription: selectedTitle?.defaultDescription || '',
      toneColor: selectedTitle?.toneColor || '#7C9FB2'
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit
  });

  // Sync editor change to Formik
  const handleEditorValueChange = (val: string) => {
    setEditorVal(val);
    formik.setFieldValue('defaultDescription', val);
  };

  // Strip HTML utility to show plain text preview
  const stripHtml = (htmlStr: string) => {
    const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
    const text = doc.body.textContent || '';
    return text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  };

  return (
    <Box className="container-fluid p-4 extra" bgcolor="#F7F5F0" minHeight="calc(100vh - 180px)">

      <Box className="d-flex mb-3 align-items-start flex-wrap justify-content-between" gap={2}>
        <Box sx={{ flex: '1 1 auto', maxWidth: '700px' }}>
          <Typography className="fw-bold" sx={{ mb: 1 }} variant="h3">
            Brief Titles
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpenAddDialog}
          className="table-row-background link-btn"
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            px: 3,
            py: 1,
            fontWeight: 'bold',
            bgcolor: 'black',
            color: 'white',
            '&:hover': {
              bgcolor: '#222'
            }
          }}
        >
          + New section
        </Button>
      </Box>

      {/* Styled Search Bar with matches counter */}
      <Box sx={{ width: '100%', mb: 4, mt: 3 }}>
        <TextField
          fullWidth
          placeholder="Search sections..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="medium"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mr: 1 }}>
                  {filteredTitles.length}/{titles.length}
                </Typography>
              </InputAdornment>
            ),
            sx: { 
              borderRadius: '25px', 
              bgcolor: 'white', 
              boxShadow: '0px 2px 5px rgba(0,0,0,0.05)',
              '& fieldset': { border: '1px solid #EBE8DF' }
            }
          }}
        />
      </Box>

      {/* Brief Title Cards */}
      <Grid container spacing={3}>
        {filteredTitles.length > 0 ? (
          filteredTitles.map((item) => {
            const iconObj = item.briefIconId as BriefIcon;
            return (
              <Grid item xs={12} sm={6} md={4} key={item.briefTitleId} sx={{ display: 'flex', minWidth: 0 }}>
                <Box
                  sx={{
                    width: '650px',
                    minWidth: 0,
                    height: '180px',
                    overflow: 'hidden',
                    bgcolor: 'white',
                    border: '1px solid #EBE8DF',
                    borderRadius: '16px',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    position: 'relative',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                      borderColor: '#75724C'
                    }
                  }}
                >
                  {/* Circular Icon Preview with Tone Color Background */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: item.toneColor || '#F5F4F0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                      border: '1px solid #0000001A',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    {iconObj?.iconImage ? (
                      <Box
                        component="img"
                        src={iconObj.iconImage}
                        alt={iconObj.iconName}
                        sx={{
                          width: 32,
                          height: 32,
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                        None
                      </Typography>
                    )}
                  </Box>

                  {/* Card Content details */}
                  <Box sx={{ flex: 1, minWidth: 0, pr: 7, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {item.kicker && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#75724C',
                          fontWeight: 600,
                          fontSize: '11px',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {item.kicker}
                      </Typography>
                    )}

                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: '#1A1A1A',
                        fontSize: '15px',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                        fontFamily: 'inherit',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '12px',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                        mt: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}
                    >
                      {stripHtml(item.defaultDescription)}
                    </Typography>
                  </Box>

                  {/* Actions in Bottom Right Corner */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      right: 10,
                      display: 'flex',
                      gap: 0.5,
                      zIndex: 2
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(item.briefTitleId)}
                      sx={{
                        p: 0.5,
                        color: '#75724C',
                        '&:hover': { bgcolor: '#F5F4F0' }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(item.briefTitleId)}
                      sx={{
                        p: 0.5,
                        color: 'error.main',
                        '&:hover': { bgcolor: '#F5F4F0' }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box sx={{ bgcolor: 'white', border: '1px solid #EBE8DF', borderRadius: '12px', p: 5, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No Brief Titles found. Click "+ New section" to add a brief title.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Live Header Icon Circular Preview with Dynamic Tone Color */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: formik.values.toneColor || '#7C9FB2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                border: '1px solid #0000001A',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
                transition: 'background-color 0.3s ease',
                flexShrink: 0
              }}
            >
              {(() => {
                const selectedIconObj = icons.find(icon => icon.briefIconId === Number(formik.values.briefIconId));
                return selectedIconObj?.iconImage ? (
                  <Box
                    component="img"
                    src={selectedIconObj.iconImage}
                    alt={selectedIconObj.iconName}
                    sx={{ width: 28, height: 28, objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                    None
                  </Typography>
                );
              })()}
            </Box>

            {/* Live Header Text */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
                {isEditMode ? 'Edit Template' : 'Add Template'}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1A1A', fontFamily: 'inherit', textTransform: 'uppercase' }}>
                {formik.values.title || 'Untitled section'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ alignSelf: 'flex-start' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers sx={{ maxHeight: '75vh', overflowY: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* TITLE */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1
                  }}
                >
                  Title *
                </Typography>
                <TextField
                  fullWidth
                  id="title"
                  name="title"
                  placeholder="Enter title..."
                  size="medium"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                      // bgcolor: '#7C9FB2',
                      '& fieldset': { border: '1px solid #EBE8DF' }
                    }
                  }}
                />
              </Box>

              {/* KICKER */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1
                  }}
                >
                  Kicker
                </Typography>
                <TextField
                  fullWidth
                  id="kicker"
                  name="kicker"
                  placeholder="Enter kicker..."
                  size="medium"
                  value={formik.values.kicker}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.kicker && Boolean(formik.errors.kicker)}
                  helperText={formik.touched.kicker && formik.errors.kicker}
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                      // bgcolor: '#7C9FB2',
                      '& fieldset': { border: '1px solid #EBE8DF' }
                    }
                  }}
                />
              </Box>

              {/* ICON */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1.5
                  }}
                >
                  Icon *
                </Typography>
                <Grid
                  container
                  spacing={1.5}
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    p: 1.5,
                    border: '1px solid #EBE8DF',
                    borderRadius: '10px',
                    bgcolor: '#F9F9F8'
                  }}
                >
                  {icons.length > 0 ? (
                    icons.map((icon) => {
                      const isSelected = formik.values.briefIconId === icon.briefIconId;
                      return (
                        <Grid item xs={3} sm={2} md={1.5} key={icon.briefIconId}>
                          <Box
                            onClick={() => formik.setFieldValue('briefIconId', icon.briefIconId)}
                            sx={{
                              cursor: 'pointer',
                              border: isSelected ? '1px solid #1A1A1A' : '1px solid #0000000A',
                              bgcolor: isSelected ? '#1A1A1A' : '#F5F4F0',
                              borderRadius: '12px',
                              p: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 64,
                              boxSizing: 'border-box',
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: isSelected ? '#1A1A1A' : '#EAE8E2'
                              }
                            }}
                          >
                            {icon.iconImage ? (
                              <Box
                                component="img"
                                src={icon.iconImage}
                                alt={icon.iconName}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  objectFit: 'contain',
                                  filter: isSelected ? 'brightness(0) invert(1)' : 'none'
                                }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                color={isSelected ? 'white' : 'text.secondary'}
                                textAlign="center"
                                noWrap
                                sx={{ fontSize: '11px' }}
                              >
                                {icon.iconName}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary" align="center" display="block">
                        No Brief Icons found. Please create one in Brief Icons first.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {formik.touched.briefIconId && formik.errors.briefIconId && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.briefIconId}
                  </Typography>
                )}
              </Box>


              {/* TONE */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1.5
                  }}
                >
                  Tone
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {tones.map((tone) => {
                    const isSelected = formik.values.toneColor === tone.value;
                    return (
                      <Box
                        key={tone.label}
                        onClick={() => formik.setFieldValue('toneColor', tone.value)}
                        sx={{
                          cursor: 'pointer',
                          px: 3,
                          py: 1,
                          borderRadius: '20px',
                          border: isSelected ? '1px solid #1A1A1A' : '1px solid #EBE8DF',
                          bgcolor: isSelected ? '#1A1A1A' : '#F5F4F0',
                          color: isSelected ? 'white' : 'black',
                          fontWeight: 600,
                          fontSize: '14px',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: isSelected ? '#1A1A1A' : '#EAE8E2'
                          }
                        }}
                      >
                        {tone.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* DEFAULT DESCRIPTION */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1.5
                  }}
                >
                  Default Description *
                </Typography>
                <Box sx={{ border: '1px solid #EBE8DF', borderRadius: '10px', overflow: 'hidden', 
                  // bgcolor: '#7C9FB2' 
                  }}>
                  <PatchedReactQuill
                    setEditorValue={handleEditorValueChange}
                    editorValue={editorVal}
                    isUpdate={false}
                  />
                </Box>
                {formik.touched.defaultDescription && formik.errors.defaultDescription && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.defaultDescription}
                  </Typography>
                )}
              </Box>

            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              sx={{ color: '#75724C', borderColor: '#75724C', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="table-row-background"
              sx={{ textTransform: 'none' }}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default BriefTitleList;
